import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useOrganization } from './OrganizationContext';
import { createAuthHeaders } from '../utils/authHeaders';

interface OrgUsage {
  limit: number;
  used: number;
  remaining: number;
  isCustom?: boolean;
}

interface OrganizationUsageContextType {
  usage: OrgUsage | null;
  refreshUsage: (orgId?: number) => Promise<void>;
}

const OrganizationUsageContext = createContext<OrganizationUsageContextType | undefined>(undefined);

export const useOrganizationUsage = () => {
  const context = useContext(OrganizationUsageContext);
  if (context === undefined) {
    throw new Error('useOrganizationUsage must be used within an OrganizationUsageProvider');
  }
  return context;
};

interface OrganizationUsageProviderProps {
  children: ReactNode;
}

export const OrganizationUsageProvider: React.FC<OrganizationUsageProviderProps> = ({ children }) => {
  const [usage, setUsage] = useState<OrgUsage | null>(null);
  const { currentOrganization, userAccess } = useOrganization();
  const { getToken } = useAuth();
  const prevOrgIdRef = useRef<number | null>(null);

  // Seed usage from currentOrganization when org changes
  useEffect(() => {
    const orgId = currentOrganization?.id ?? null;
    if (orgId !== prevOrgIdRef.current) {
      prevOrgIdRef.current = orgId;
      if (currentOrganization?.usage) {
        setUsage(currentOrganization.usage);
      } else {
        setUsage(null);
      }
    }
  }, [currentOrganization?.id, currentOrganization?.usage]);

  const createAuthHeadersCallback = useCallback(async (): Promise<HeadersInit> => {
    return createAuthHeaders(getToken);
  }, [getToken]);

  const refreshUsage = useCallback(async (orgId?: number) => {
    const targetOrgId = orgId || currentOrganization?.id;

    if (!userAccess || !targetOrgId) {
      return;
    }

    try {
      const headers = await createAuthHeadersCallback();

      const response = await fetch(`/api/organizations/${targetOrgId}/usage`, {
        headers,
      });

      if (!response.ok) {
        if (response.status === 404 || response.status === 403) {
          console.warn(`Usage endpoint not available (${response.status}) for org ${targetOrgId}`);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: any = await response.json();

      setUsage(prev => {
        // Only update if this is for the current organization
        if (currentOrganization?.id !== targetOrgId) return prev;
        // Skip update if usage hasn't changed (prevents unnecessary re-renders)
        if (prev &&
            prev.used === data.usage.used &&
            prev.limit === data.usage.limit &&
            prev.remaining === data.usage.remaining) {
          return prev;
        }
        return data.usage;
      });
    } catch (error) {
      console.error('Error refreshing organization usage:', error);
    }
  }, [userAccess?.userId, currentOrganization?.id, createAuthHeadersCallback]);

  const contextValue: OrganizationUsageContextType = {
    usage,
    refreshUsage,
  };

  return (
    <OrganizationUsageContext.Provider value={contextValue}>
      {children}
    </OrganizationUsageContext.Provider>
  );
};
